import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useWatch } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Control } from 'react-hook-form/dist/types/form';
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import { formatCurrency, formatPercentage } from 'utils';
import { CreditSubmission, Proposal, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { LenderApproval } from 'components/CompareApprovals/types';
import { InputField } from 'components/form/rhf/InputField';
import { ApprovalNotesFormValues } from '../../types';
import { SectionTitle } from './SectionTitle';
import { SubSectionTitle } from './SubSectionTitle';
import { ApprovalTermRow } from './ApprovalTermRow';

type SaveFieldOnBlur = (
  fieldName: 'transactionSummary' | 'lenderNotes' | 'dealerNotes' | 'creditSubStage',
  value: string | number | null | undefined,
  successMessage: string
) => Promise<void>;

interface Props {
  creditSubmission: CreditSubmission & { proposals?: Proposal[]; contingencies: string[] };
  vo: VendorOpportunity;
  approval: LenderApproval | null;
  formControl: Control<ApprovalNotesFormValues>;
  onSaveField: SaveFieldOnBlur;
}

export const ApprovalOverviewCard = ({ creditSubmission, vo, approval, formControl: control, onSaveField }: Props) => {
  const history = useHistory();

  const classes = useStyles();

  const transactionSummary = useWatch({ control, name: 'transactionSummary' });
  const lenderNotes = useWatch({ control, name: 'lenderNotes' });
  const dealerNotes = useWatch({ control, name: 'dealerNotes' });

  const proposalId = creditSubmission.proposals?.[0]?.proposalId;

  const handleCreateProposal = () => {
    if (!proposalId) return;
    history.push({
      hash: '#desking',
      pathname: `/vo/${vo.vendorOpportunityId}`,
      state: { proposalId },
    });
  };

  const proposalFinance = creditSubmission.proposals?.[0]?.createdSnapshot?.paymentOptions?.[0]?.finance;
  const pricing = creditSubmission.proposals?.[0]?.createdSnapshot?.settings?.configuration?.pricing;
  const approvalTerms = approval?.approvalTerms;
  const docFeeTotal = (approvalTerms?.lenderFees ?? [])
    .filter((f: any) => f?.feeName?.toLowerCase()?.includes('doc'))
    .reduce((sum: number, f: any) => sum + (f?.amount ?? 0), 0);

  return (
    <Paper elevation={0} className={classes.root}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, alignItems: 'center' }}>
        <SectionTitle>Approval Overview</SectionTitle>
        <Button size="small" variant="outlined" sx={{ justifySelf: 'flex-end' }} startIcon={<EditIcon />}>
          Edit Approval Overview
        </Button>

        <Stack direction="row" gap={1} justifyContent="space-between">
          <Box className={classes.amountCardsContainer}>
            <Typography variant="body2" color="text.secondary">
              Opportunity Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(vo.invoiceTotal)}</Typography>
          </Box>

          <Box className={classes.amountCardsContainer}>
            <Typography variant="body2" color="text.secondary">
              Requested Amount
            </Typography>
            <Typography variant="h6">{formatCurrency(creditSubmission.amountRequested)}</Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
        <Stack gap={0.5}>
          <SubSectionTitle>Approval Terms</SubSectionTitle>
          <ApprovalTermRow label="Approved Amount" value={formatCurrency(creditSubmission.approvedAmount)} />
          <ApprovalTermRow label="Finance Program" value={proposalFinance?.financeProgramName || '—'} />
          <ApprovalTermRow
            label="Interest Rate"
            value={approvalTerms?.interestRate ? formatPercentage(approvalTerms?.interestRate) : '—'}
          />
          <ApprovalTermRow label="Down Payment" value={approvalTerms?.minimumRequiredDownPayment || '—'} />
          <ApprovalTermRow label="Term" value={(approvalTerms?.termLengthMonths as unknown as string) || '—'} />
          <ApprovalTermRow label="Doc Fees" value={docFeeTotal ? formatCurrency(docFeeTotal) : '—'} />
        </Stack>

        <Stack gap={0.5}>
          <SubSectionTitle>Options</SubSectionTitle>
          <Stack direction="row" gap={1}>
            {pricing &&
              Object.values(pricing).length > 0 &&
              Object.values(pricing).map((option: any) => (
                <Box
                  key={option.term}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: '#F5F5F5',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                  }}
                >
                  <Typography variant="h5">
                    {option.paymentAmountPerPeriod != null ? formatCurrency(option.paymentAmountPerPeriod) : '—'}
                    /mo.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    for {option.term ?? '—'} months
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Stack>

        <Stack gap={0.5}>
          <SubSectionTitle>Conditions &amp; Stipulations</SubSectionTitle>
          {creditSubmission.contingencies?.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'rgba(0,0,0,0.12)',
              }}
            >
              <Typography variant="body1">{item}</Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
        <Stack gap={2}>
          <InputField
            name="transactionSummary"
            label="Transaction Summary"
            control={control}
            inputProps={{
              variant: 'outlined',
              onBlur: () => onSaveField('transactionSummary', transactionSummary, 'Transaction Summary saved'),
            }}
          />
          <InputField
            name="lenderNotes"
            label="Notes from Lender"
            control={control}
            inputProps={{
              variant: 'outlined',
              multiline: true,
              rows: 3,
              onBlur: () => onSaveField('lenderNotes', lenderNotes, 'Lender Notes saved'),
            }}
          />
          {/*           <InputField
            name="dealerNotes"
            label="Dealer Notes"
            control={control}
            inputProps={{
              variant: 'outlined',
              multiline: true,
              rows: 3,
              onBlur: () => onSaveField('dealerNotes', dealerNotes, 'Dealer Notes saved'),
            }}
          /> */}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle1" component="span" fontWeight={600}>
          Ready to proceed?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Create a quote using these approval terms.
        </Typography>
        <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleCreateProposal}>
          Create Quote
        </Button>
      </Box>
    </Paper>
  );
};

const useStyles = makeStyles({
  root: {
    boxShadow: '0px 2px 12px 0px rgba(0,0,0,0.1)',
    padding: '16px 24px',
    gap: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
  amountCardsContainer: {
    padding: '8px 16px',
    borderRadius: 1,
    backgroundColor: 'rgba(227, 242, 253, 0.7)',
    boxShadow: '0px 4px 8px -1px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: '8px',
  },
});
